import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { resolveTabAccess } from '../auth/pageAccess';
import { NavLink, useNavigate } from 'react-router-dom';

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  LabelList,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import AdminNavTabs from '../components/AdminNavTabs';
import { useAdminCapability } from '../components/useAdminCapability';
import { formatMinor, formatMajor, currencySymbol } from '../utils/money';
import {
  STORE,
  ONLINE,
  MARKETPLACE,
  MIN_ACTIVE_HOURS,
  salesChannelOf,
  channelSplit,
  settlementSplit,
  paymentTypeLabel,
  storeMetrics,
  serverMetrics,
  sortServers,
  storeTopItems,
} from '../utils/salesMetrics';
import './AdminDashboard.css';

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

const pad2 = (n) => String(n).padStart(2, '0');
const toDateInput = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const safeArray = (val) => (Array.isArray(val) ? val : []);

const parseDollarsToCents = (val) => {
  try {
    if (val === null || val === undefined) return 0;
    if (typeof val === 'number' && Number.isFinite(val)) return Math.max(0, Math.round(val * 100));
    const s = String(val).trim();
    if (!s) return 0;
    const cleaned = s.startsWith('$') ? s.slice(1) : s;
    const n = Number(cleaned);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.round(n * 100));
  } catch {
    return 0;
  }
};

const formatMoneyFromCents = (value) => {
  const n = Number(value);
  return formatMinor(Number.isFinite(n) ? n : 0);
};

// The new store/server sections deal in metrics that can legitimately be
// UNAVAILABLE rather than zero (spend per cover with no covers recorded, a rate
// with an empty denominator). These render an em dash, never "0%" or "NaN" — a
// zero would read as a real measurement on a page used to decide someone's perk.
const DASH = '—';

const formatPercent = (ratio, digits = 0) => {
  const n = Number(ratio);
  if (ratio == null || !Number.isFinite(n)) return DASH;
  return `${(n * 100).toFixed(digits)}%`;
};

const formatMoneyOrDash = (cents) => {
  const n = Number(cents);
  if (cents == null || !Number.isFinite(n)) return DASH;
  return formatMinor(n);
};

const formatHours = (hours) => {
  const n = Number(hours);
  if (hours == null || !Number.isFinite(n)) return DASH;
  return `${n.toFixed(1)}h`;
};

const formatAge = (hours) => {
  const n = Number(hours);
  if (hours == null || !Number.isFinite(n)) return DASH;
  if (n < 1) return `${Math.max(1, Math.round(n * 60))}m`;
  return `${n.toFixed(1)}h`;
};

const CHANNEL_LABELS = {
  [STORE]: 'Store (Take Order)',
  [ONLINE]: 'Online',
  [MARKETPLACE]: 'Marketplace',
};

const CHANNEL_COLORS = {
  [STORE]: '#0ea5e9',
  [ONLINE]: '#22c55e',
  [MARKETPLACE]: '#f59e0b',
};

// Payment-type slice colors for the settlement donut. Keyed by the settlementSplit
// keys (CASH / provider names / UNPAID). Cash is green (money in the drawer); the card
// providers get distinct hues; unpaid is grey so it reads as "not collected yet".
const PAYMENT_TYPE_COLORS = {
  CASH: '#16a34a',
  STRIPE: '#635bff',
  SQUARE: '#3b82f6',
  CLOVER: '#0ea5e9',
  PHONEPE: '#7c3aed',
  RAZORPAY: '#0f766e',
  UNPAID: '#94a3b8',
  OTHER: '#f59e0b',
};

// Fulfillment slice colors for the settlement donut. Fulfillment keys are free text
// from the orders (e.g. "Pickup complete", "In progress"), so this is a fixed palette
// assigned by index rather than a fixed key map.
const FULFILLMENT_PALETTE = ['#0ea5e9', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#f43f5e', '#64748b'];

// Leaderboard columns. `perks: true` marks the three metrics the owner chose as
// the perks basis (D3) so the page itself shows the intended incentive basis
// rather than leaving it in a document nobody reads. Void rate sits beside the
// two reward metrics deliberately: it is the integrity counterweight, so a
// scheme cannot be gamed by over-ordering and then removing.
const SERVER_COLUMNS = [
  { key: 'serverName', label: 'Server', kind: 'text' },
  { key: 'orders', label: 'Orders', kind: 'number' },
  { key: 'salesCents', label: 'Revenue', kind: 'money' },
  { key: 'avgOrderValueCents', label: 'Avg order', kind: 'money' },
  { key: 'covers', label: 'Covers', kind: 'number' },
  { key: 'spendPerCoverCents', label: 'Per cover', kind: 'money' },
  { key: 'upsellRate', label: 'Upsell rate', kind: 'percent', perks: true },
  { key: 'valueAddedCents', label: 'Value added', kind: 'money' },
  { key: 'revenuePerActiveHourCents', label: 'Revenue / active hour', kind: 'money', perks: true },
  { key: 'voidRate', label: 'Void rate', kind: 'percent', perks: true },
  { key: 'valueRemovedCents', label: 'Value removed', kind: 'money' },
  { key: 'activeHours', label: 'Active hours', kind: 'hours' },
];

const escapeCsv = (val) => {
  const s = String(val ?? '');
  if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const downloadCsv = ({ filename, rows }) => {
  const header = rows.length ? Object.keys(rows[0]) : [];
  const lines = [header.map(escapeCsv).join(',')];
  for (const row of rows) {
    lines.push(header.map((k) => escapeCsv(row?.[k])).join(','));
  }
  const csv = `${lines.join('\n')}\n`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const easternFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  hour12: false,
});

const bucketKey = (epochSeconds, granularity) => {
  try {
    const dt = new Date(Number(epochSeconds) * 1000);
    if (Number.isNaN(dt.getTime())) return '';
    const parts = easternFormatter.formatToParts(dt);
    const year = parts.find((p) => p.type === 'year')?.value || '';
    const month = parts.find((p) => p.type === 'month')?.value || '';
    const day = parts.find((p) => p.type === 'day')?.value || '';
    const hour = parts.find((p) => p.type === 'hour')?.value || '';
    if (!year || !month || !day) return '';
    const dateKey = `${year}-${month}-${day}`;
    if (granularity === 'hour') {
      if (!hour) return '';
      return `${dateKey} ${hour}:00`;
    }
    return dateKey;
  } catch {
    return '';
  }
};

const sumCents = (orders) => {
  return safeArray(orders).reduce((acc, o) => {
    const cents = o?.amount_total_cents ?? o?.amount_total;
    const n = Number(cents);
    return acc + (Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0);
  }, 0);
};

const normalizeChannel = (order) => {
  const channel = String(order?.salesChannel || '').trim().toUpperCase();
  if (channel) return channel;
  return order?.isPos ? 'POS' : 'ONLINE';
};

const normalizePayType = (order) => {
  const isPos = Boolean(order?.isPos);
  const mode = String(order?.posPaymentMode || '').trim().toUpperCase();
  return isPos && mode === 'PAY_AT_POS' ? 'pay_at_store' : 'pay_online';
};

export default function AdminOnlineSalesReport() {
  const navigate = useNavigate();
  const statusRef = useRef(null);

  // Per-server performance data is management-only (D4). Gated on ROLE via
  // canEditTab, never on canEdit: the per-tab permission map is `{}` for
  // Admin/Superadmin, so canEdit('Orders/Reports') is always false for exactly
  // the people entitled to see this — the trap that already hid the Take Order
  // "+ Add items" link and the Cancel Order button on this codebase.
  const capability = useAdminCapability();
  const canSeeServerReport = capability.isAdminRole;

  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');

  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const [rangeMode, setRangeMode] = useState('lookback');
  const [lookbackUnit, setLookbackUnit] = useState('days');
  const [lookbackValue, setLookbackValue] = useState('7');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [orderStatusFilter, setOrderStatusFilter] = useState('ALL');
  const [channelFilter, setChannelFilter] = useState('ALL');
  // Store/Online/Marketplace narrowing (R3.3). A SEPARATE filter from
  // `channelFilter` above, which narrows on the raw `salesChannel` attribute
  // (POS/ONLINE) and cannot tell a waiter's ticket from a customer ordering at
  // the counter — both are "POS". Left at ALL this changes nothing.
  const [dimFilter, setDimFilter] = useState('ALL');
  const [serverSort, setServerSort] = useState({ column: 'salesCents', direction: 'desc' });
  const [payTypeFilter, setPayTypeFilter] = useState('ALL');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [mealFilter, setMealFilter] = useState('ALL');
  const [locationFilter, setLocationFilter] = useState('ALL');
  const [locations, setLocations] = useState([]);
  const [granularity, setGranularity] = useState('auto');

  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [searching, setSearching] = useState(false);

  const [topMode, setTopMode] = useState('revenue');

  useEffect(() => {
    if (!statusMessage && !error) return;
    try {
      window.setTimeout(() => {
        try {
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
          statusRef.current?.focus?.({ preventScroll: true });
        } catch {
          // ignore
        }
      }, 50);
    } catch {
      // ignore
    }
  }, [statusMessage, error]);

  useEffect(() => {
    if (rangeMode !== 'lookback') return;
    try {
      const raw = Number(lookbackValue);
      const v = Number.isFinite(raw) ? raw : 7;
      const unit = lookbackUnit === 'hours' ? 'hours' : 'days';
      const ms = unit === 'hours' ? v * 3600 * 1000 : v * 24 * 3600 * 1000;
      const end = new Date();
      const start = new Date(Date.now() - ms);
      setEndDate(toDateInput(end));
      setStartDate(toDateInput(start));
    } catch {
      // ignore
    }
  }, [rangeMode, lookbackUnit, lookbackValue]);

  const statusOptions = useMemo(
    () => [
      { k: 'ALL', label: 'ALL' },
      { k: 'processing', label: 'Processing' },
      { k: 'shipped', label: 'Shipped' },
      { k: 'cancelled', label: 'Cancelled' },
      { k: 'refunded', label: 'Refunded' },
      { k: 'open', label: 'Open (POS)' },
      { k: 'ready_for_pickup', label: 'Ready for pickup (POS)' },
      { k: 'pickup_complete', label: 'Pickup complete (POS)' },
      { k: 'pending_payment', label: 'Pending payment (POS)' },
      { k: 'paid', label: 'Paid (POS)' },
    ],
    []
  );

  const productById = useMemo(() => {
    const out = {};
    safeArray(products).forEach((p) => {
      const pid = String(p?.ProductID || p?.productId || '').trim();
      if (!pid) return;
      out[pid] = p;
    });
    return out;
  }, [products]);

  const availableCategories = useMemo(() => {
    const uniq = new Set();
    for (const p of safeArray(products)) {
      const cat = String(p?.Category || p?.category || '').trim();
      if (cat) uniq.add(cat);
    }
    return ['ALL', ...Array.from(uniq).sort((a, b) => a.localeCompare(b))];
  }, [products]);

  const availableMeals = useMemo(() => {
    const uniq = new Set();
    for (const p of safeArray(products)) {
      const raw = p?.AvailableDuring ?? p?.availableDuring ?? p?.available_during ?? p?.availableDuringWindows;
      safeArray(raw)
        .map((x) => String(x || '').trim())
        .filter(Boolean)
        .forEach((v) => uniq.add(v));
    }
    return ['ALL', ...Array.from(uniq).sort((a, b) => a.localeCompare(b))];
  }, [products]);

  const channels = useMemo(() => {
    const uniq = new Set();
    safeArray(orders).forEach((o) => {
      const c = normalizeChannel(o);
      if (c) uniq.add(c);
    });
    return ['ALL', ...Array.from(uniq).sort((a, b) => a.localeCompare(b))];
  }, [orders]);

  const resolvedGranularity = useMemo(() => {
    if (granularity === 'hour' || granularity === 'day') return granularity;
    try {
      const start = new Date(`${startDate}T00:00:00`);
      const end = new Date(`${endDate}T00:00:00`);
      const diff = end.getTime() - start.getTime();
      const days = diff / (24 * 3600 * 1000);
      return days <= 2 ? 'hour' : 'day';
    } catch {
      return 'day';
    }
  }, [granularity, startDate, endDate]);

  const fetchProducts = React.useCallback(async (token) => {
    if (!API_BASE_URL) return;
    const res = await fetch(`${API_BASE_URL}/admin/products`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || 'Failed to fetch products');
    setProducts(Array.isArray(data?.products) ? data.products : []);
  }, []);

  const runReport = React.useCallback(async () => {
    setError('');
    setStatusMessage('');

    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }

    if (!startDate || !endDate) {
      setError('Please set start and end date.');
      return;
    }

    try {
      setSearching(true);
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      await fetchProducts(token);

      const url = new URL(`${API_BASE_URL}/admin/orders/report`);
      url.searchParams.set('start', startDate);
      url.searchParams.set('end', endDate);
      url.searchParams.set('include_items', '1');
      if (orderStatusFilter && orderStatusFilter !== 'ALL') {
        url.searchParams.set('status', orderStatusFilter);
      }
      if (locationFilter && locationFilter !== 'ALL') {
        url.searchParams.set('location_id', locationFilter);
      }

      const res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to run report');

      const list = Array.isArray(data?.orders) ? data.orders : [];
      setOrders(list);
      setStatusMessage(`Loaded ${list.length} order(s).`);
    } catch (e) {
      setOrders([]);
      setError(e?.message || 'Failed to run report');
    } finally {
      setSearching(false);
    }
  }, [startDate, endDate, orderStatusFilter, fetchProducts]);

  useEffect(() => {
    if (loading) return;
    runReport();
  }, [loading, runReport]);

  const filteredOrders = useMemo(() => {
    let out = safeArray(orders);

    if (channelFilter && channelFilter !== 'ALL') {
      out = out.filter((o) => normalizeChannel(o) === channelFilter);
    }

    if (dimFilter && dimFilter !== 'ALL') {
      out = out.filter((o) => salesChannelOf(o) === dimFilter);
    }

    if (payTypeFilter && payTypeFilter !== 'ALL') {
      out = out.filter((o) => {
        const pt = normalizePayType(o);
        return payTypeFilter === 'PAY_AT_STORE' ? pt === 'pay_at_store' : pt === 'pay_online';
      });
    }

    if ((categoryFilter && categoryFilter !== 'ALL') || (mealFilter && mealFilter !== 'ALL')) {
      out = out.filter((o) => {
        const items = safeArray(o?.items);
        if (!items.length) return false;

        return items.some((it) => {
          const pid = String(it?.ProductID || it?.productId || '').trim();
          const product = pid ? productById[pid] : null;

          if (categoryFilter && categoryFilter !== 'ALL') {
            const cat = String(product?.Category || product?.category || '').trim();
            if (!cat || cat !== categoryFilter) return false;
          }

          if (mealFilter && mealFilter !== 'ALL') {
            const raw = product?.AvailableDuring ?? product?.availableDuring ?? product?.available_during ?? product?.availableDuringWindows;
            const list = safeArray(raw).map((x) => String(x || '').trim()).filter(Boolean);
            if (!list.some((v) => v.toLowerCase() === mealFilter.toLowerCase())) return false;
          }

          return true;
        });
      });
    }

    return out;
  }, [orders, channelFilter, dimFilter, payTypeFilter, categoryFilter, mealFilter, productById]);

  const totalSalesCents = useMemo(() => sumCents(filteredOrders), [filteredOrders]);
  const totalOrders = useMemo(() => filteredOrders.length, [filteredOrders]);
  const avgOrderValueCents = useMemo(() => (totalOrders ? Math.round(totalSalesCents / totalOrders) : 0), [totalOrders, totalSalesCents]);

  const paySplit = useMemo(() => {
    const out = {
      pay_online: { orders: 0, sales_cents: 0 },
      pay_at_store: { orders: 0, sales_cents: 0 },
    };
    for (const o of filteredOrders) {
      const pt = normalizePayType(o);
      const cents = o?.amount_total_cents ?? o?.amount_total;
      const n = Number(cents);
      const add = Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
      out[pt].orders += 1;
      out[pt].sales_cents += add;
    }
    return out;
  }, [filteredOrders]);

  const fulfillmentSplit = useMemo(() => {
    const out = {
      delivery: { orders: 0, sales_cents: 0 },
      pickup: { orders: 0, sales_cents: 0 },
    };
    for (const o of filteredOrders) {
      // Check fulfillment_type, or fallback to checking if delivery_address exists
      let ft = String(o?.fulfillment_type || '').trim().toLowerCase();
      if (!ft && o?.delivery_address) {
        ft = 'delivery';
      }
      if (!ft) {
        ft = 'pickup';
      }
      const key = ft === 'delivery' ? 'delivery' : 'pickup';
      const cents = o?.amount_total_cents ?? o?.amount_total;
      const n = Number(cents);
      const add = Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
      out[key].orders += 1;
      out[key].sales_cents += add;
    }
    return out;
  }, [filteredOrders]);

  const unpaidOrders = useMemo(() => {
    return filteredOrders.filter((o) => {
      if (normalizePayType(o) !== 'pay_at_store') return false;
      const pay = String(o?.displayPaymentStatus || o?.payment_status || o?.paymentStatus || '').trim().toUpperCase();
      return pay === 'PENDING_PAYMENT' || !pay;
    }).length;
  }, [filteredOrders]);

  const paymentDonutData = useMemo(() => {
    return [
      { name: 'Pay Online', value: paySplit.pay_online.sales_cents, key: 'pay_online' },
      { name: 'Pay at Store', value: paySplit.pay_at_store.sales_cents, key: 'pay_at_store' },
    ];
  }, [paySplit]);

  const fulfillmentDonutData = useMemo(() => {
    return [
      { name: 'Delivery', value: fulfillmentSplit.delivery.sales_cents, key: 'delivery' },
      { name: 'Pickup', value: fulfillmentSplit.pickup.sales_cents, key: 'pickup' },
    ];
  }, [fulfillmentSplit]);

  const trendSeries = useMemo(() => {
    const map = new Map();
    for (const o of filteredOrders) {
      const ts = o?.paid_at || o?.order_timestamp;
      const key = bucketKey(ts, resolvedGranularity);
      if (!key) continue;

      const existing = map.get(key) || {
        bucket: key,
        payOnlineRevenueCents: 0,
        payAtStoreRevenueCents: 0,
        deliveryRevenueCents: 0,
        pickupRevenueCents: 0,
        orders: 0,
      };

      const cents = o?.amount_total_cents ?? o?.amount_total;
      const n = Number(cents);
      const add = Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
      
      // Payment type split
      if (normalizePayType(o) === 'pay_at_store') existing.payAtStoreRevenueCents += add;
      else existing.payOnlineRevenueCents += add;
      
      // Fulfillment type split - check fulfillment_type or fallback to delivery_address
      let ft = String(o?.fulfillment_type || '').trim().toLowerCase();
      if (!ft && o?.delivery_address) {
        ft = 'delivery';
      }
      if (!ft) {
        ft = 'pickup';
      }
      if (ft === 'delivery') existing.deliveryRevenueCents += add;
      else existing.pickupRevenueCents += add;
      
      existing.orders += 1;

      map.set(key, existing);
    }

    const list = Array.from(map.values());
    list.sort((a, b) => String(a.bucket).localeCompare(String(b.bucket)));
    return list.map((r) => ({
      bucket: r.bucket,
      payOnlineRevenue: r.payOnlineRevenueCents / 100,
      payAtStoreRevenue: r.payAtStoreRevenueCents / 100,
      deliveryRevenue: r.deliveryRevenueCents / 100,
      pickupRevenue: r.pickupRevenueCents / 100,
      orders: r.orders,
      revenue: (r.payOnlineRevenueCents + r.payAtStoreRevenueCents) / 100,
    }));
  }, [filteredOrders, resolvedGranularity]);

  const comboSeries = useMemo(() => {
    return trendSeries.map((r) => ({ bucket: r.bucket, orders: r.orders, revenue: r.revenue }));
  }, [trendSeries]);

  const topItems = useMemo(() => {
    const map = new Map();

    for (const o of filteredOrders) {
      for (const it of safeArray(o?.items)) {
        const pid = String(it?.ProductID || it?.productId || '').trim();
        const key = pid || String(it?.name || it?.ProductName || '').trim();
        if (!key) continue;

        const qtyRaw = it?.quantity ?? it?.Quantity;
        const qty = Number.isFinite(Number(qtyRaw)) ? Math.max(0, Math.floor(Number(qtyRaw))) : 0;

        const prod = pid ? productById[pid] : null;

        const baseFromItemCents = Number(
          it?.price_cents ?? it?.priceCents ?? it?.unitPriceCents ?? it?.unit_price_cents ?? 0
        );
        const baseFromItemDollars = parseDollarsToCents(it?.price ?? it?.Price ?? it?.unitPrice ?? it?.UnitPrice);
        const baseFromCatalogDollars = parseDollarsToCents(prod?.Price ?? prod?.price);

        let base = Number.isFinite(baseFromItemCents) ? baseFromItemCents : 0;
        if (!base || base <= 0) base = baseFromItemDollars;
        if (!base || base <= 0) base = baseFromCatalogDollars;

        const mod = Number(it?.modifier_price_cents ?? it?.modifierPriceCents ?? 0);
        const unit = (Number.isFinite(base) ? base : 0) + (Number.isFinite(mod) ? mod : 0);
        const revenue = qty * Math.max(0, Math.floor(unit));

        const existing = map.get(key) || {
          key,
          productId: pid,
          name: '',
          quantity: 0,
          revenueCents: 0,
          category: '',
          meals: '',
        };

        existing.quantity += qty;
        existing.revenueCents += revenue;

        const displayName = String(prod?.ProductName || prod?.name || it?.name || key).trim();
        if (displayName) existing.name = displayName;
        if (prod) {
          const cat = String(prod?.Category || prod?.category || '').trim();
          if (cat) existing.category = cat;
          const rawMeals = prod?.AvailableDuring ?? prod?.availableDuring ?? prod?.available_during ?? prod?.availableDuringWindows;
          const meals = safeArray(rawMeals).map((x) => String(x || '').trim()).filter(Boolean);
          if (meals.length) existing.meals = meals.join(' | ');
        }

        map.set(key, existing);
      }
    }

    const list = Array.from(map.values());
    list.sort((a, b) => Number(b.revenueCents) - Number(a.revenueCents));
    return list.slice(0, 12);
  }, [filteredOrders, productById]);

  const topItemsChartData = useMemo(() => {
    return topItems.map((it, idx) => {
      const fullName = it.name || it.key;
      const safeName = String(fullName || '').trim() || `Item ${idx + 1}`;
      const short = safeName.length > 40 ? `${safeName.slice(0, 37)}...` : safeName;
      return {
        rank: idx + 1,
        label: `${idx + 1}. ${short}`,
        fullName: safeName,
        revenue: Number((it.revenueCents || 0) / 100),
        quantity: Number(it.quantity || 0),
      };
    });
  }, [topItems]);

  const topItemsChartHeight = useMemo(() => {
    const rows = topItemsChartData.length || 0;
    return Math.max(340, rows * 34);
  }, [topItemsChartData.length]);

  const topItemsChartDataForDisplay = useMemo(() => {
    const list = safeArray(topItemsChartData);
    if (topMode === 'quantity') {
      return list.filter((r) => Number(r?.quantity || 0) > 0);
    }
    return list.filter((r) => Number(r?.revenue || 0) > 0);
  }, [topItemsChartData, topMode]);

  const topItemsChartHeightForDisplay = useMemo(() => {
    const rows = topItemsChartDataForDisplay.length || 0;
    return Math.max(340, rows * 34);
  }, [topItemsChartDataForDisplay.length]);

  // ---------------------------------------------------------------------------
  // Store + per-server sections (sales-report-store-and-server spec).
  //
  // All arithmetic lives in utils/salesMetrics.js and is unit-tested there. These
  // memos only shape it for rendering, so nothing above this line changes
  // behaviour: every pre-existing KPI, split, trend and export is untouched.
  // ---------------------------------------------------------------------------

  const channelStats = useMemo(() => channelSplit(filteredOrders), [filteredOrders]);

  const channelRows = useMemo(
    () => [STORE, ONLINE, MARKETPLACE].map((key) => ({
      key,
      name: CHANNEL_LABELS[key],
      orders: channelStats[key].orders,
      value: channelStats[key].salesCents,
      share: channelStats.totalSalesCents > 0 ? channelStats[key].salesCents / channelStats.totalSalesCents : null,
    })),
    [channelStats],
  );

  // Chart series carry MAJOR units (rupees/dollars), matching the page's existing
  // trendSeries convention, so `formatMajor` is correct on both the axis and the
  // tooltip. The tables below stay in cents and use formatMoneyFromCents. Mixing
  // the two is how an axis ends up reading 100× the real figure.
  const channelTrend = useMemo(() => {
    const map = new Map();
    for (const o of filteredOrders) {
      const bucket = bucketKey(o?.order_timestamp, resolvedGranularity);
      if (!bucket) continue;
      const row = map.get(bucket) || { bucket, [STORE]: 0, [ONLINE]: 0, [MARKETPLACE]: 0 };
      const cents = Number(o?.amount_total_cents ?? o?.amount_total);
      row[salesChannelOf(o)] += (Number.isFinite(cents) ? Math.max(0, Math.floor(cents)) : 0) / 100;
      map.set(bucket, row);
    }
    return Array.from(map.values()).sort((a, b) => String(a.bucket).localeCompare(String(b.bucket)));
  }, [filteredOrders, resolvedGranularity]);

  // Settlement split (cash-payment-reporting): how the period's revenue was PAID and
  // how it was FULFILLED, for end-of-day reconciliation. Cancelled excluded; unpaid shown
  // in the payment split but never counted as collected. Pure arithmetic in salesMetrics.
  const settlement = useMemo(() => settlementSplit(filteredOrders), [filteredOrders]);

  // Donut series for the settlement section. Money in MAJOR units (value/100) so the
  // pie tooltip reuses formatMajor like the channel donut. UNPAID is excluded from the
  // payment donut — a donut is "share of what was collected", and unpaid money was not.
  // Zero/negative slices are dropped so the donut never renders an empty wedge.
  const paymentSplitDonutData = useMemo(
    () => (settlement.byPayment || [])
      .filter((r) => r.key !== 'UNPAID' && r.salesCents > 0)
      .map((r) => ({ key: r.key, name: paymentTypeLabel(r.key), value: r.salesCents / 100 })),
    [settlement],
  );

  const fulfillmentSplitDonutData = useMemo(
    () => (settlement.byFulfillment || [])
      .filter((r) => r.salesCents > 0)
      .map((r) => ({ key: r.key, name: r.key, value: r.salesCents / 100 })),
    [settlement],
  );

  const storeStats = useMemo(() => storeMetrics(filteredOrders), [filteredOrders]);
  const storeItems = useMemo(() => storeTopItems(filteredOrders, { productById, limit: 10 }), [filteredOrders, productById]);
  const { servers: serverRows, additions: additionRows } = useMemo(
    () => serverMetrics(filteredOrders),
    [filteredOrders],
  );

  const sortedServers = useMemo(
    () => sortServers(serverRows, serverSort.column, serverSort.direction),
    [serverRows, serverSort],
  );

  const hasStoreOrders = storeStats.orders > 0 || storeStats.cancelled.orders > 0;

  // --- Chart series for the store + server sections -------------------------
  //
  // Every chart sits BESIDE its table rather than replacing it. The chart answers
  // "who/what stands out"; the table is what someone reads out when a number is
  // disputed, and for a perks conversation the exact figure is the thing that
  // matters. Money series are in major units (see channelTrend above); rate series
  // are whole percentages, so their axes and tooltips use % rather than currency.

  const channelDonutData = useMemo(
    () => channelRows
      .filter((r) => r.value > 0)
      .map((r) => ({ key: r.key, name: r.name, value: r.value / 100 })),
    [channelRows],
  );

  const storeMixDonutData = useMemo(() => ([
    { key: 'dine_in', name: 'Dine-in', value: storeStats.dineIn.salesCents / 100 },
    { key: 'to_go', name: 'To-go', value: storeStats.toGo.salesCents / 100 },
  ].filter((r) => r.value > 0)), [storeStats]);

  const STORE_MIX_COLORS = { dine_in: '#0ea5e9', to_go: '#f472b6' };

  const tableChartData = useMemo(
    () => storeStats.tables.slice(0, 12).map((t) => ({
      label: `Table ${t.tableLabel}`,
      revenue: t.salesCents / 100,
      turns: t.turns,
    })),
    [storeStats],
  );

  const storeItemsChartData = useMemo(
    () => storeItems.map((it, idx) => {
      const full = String(it.name || it.key || '').trim() || `Item ${idx + 1}`;
      return {
        label: full.length > 26 ? `${full.slice(0, 23)}…` : full,
        fullName: full,
        revenue: it.revenueCents / 100,
        quantity: it.quantity,
      };
    }),
    [storeItems],
  );

  const unpaidChartData = useMemo(
    () => storeStats.unpaid.buckets.map((b) => ({
      label: b.label,
      tickets: b.orders,
      value: b.salesCents / 100,
    })),
    [storeStats],
  );

  // One row per server, in the order the leaderboard is currently sorted, so the
  // chart and the table always agree on ranking.
  const serverChartData = useMemo(
    () => sortedServers.map((s) => ({
      label: s.serverName,
      revenue: s.salesCents / 100,
      valueAdded: s.valueAddedCents / 100,
      valueRemoved: s.valueRemovedCents / 100,
      orders: s.orders,
      // Rates as whole percentages. `null` stays null so recharts draws a gap
      // rather than a zero bar for a metric that is unavailable.
      upsellPct: s.upsellRate == null ? null : Math.round(s.upsellRate * 1000) / 10,
      voidPct: s.voidRate == null ? null : Math.round(s.voidRate * 1000) / 10,
      perHour: s.revenuePerActiveHourCents == null ? null : s.revenuePerActiveHourCents / 100,
    })),
    [sortedServers],
  );

  const serverTopItemsChart = useMemo(
    () => sortedServers.map((s) => ({
      serverKey: s.serverKey,
      serverName: s.serverName,
      rows: s.topItems.map((it) => {
        const full = String(it.name || it.key || '').trim();
        return {
          label: full.length > 20 ? `${full.slice(0, 17)}…` : full,
          fullName: full,
          revenue: it.revenueCents / 100,
          quantity: it.quantity,
        };
      }),
    })),
    [sortedServers],
  );

  const editsChartData = useMemo(
    () => additionRows.map((a) => ({
      label: a.name,
      valueAdded: a.valueAddedCents / 100,
      valueRemoved: a.valueRemovedCents / 100,
    })),
    [additionRows],
  );

  // Horizontal bar charts need height proportional to row count or the labels
  // collide; 34px per row is the figure the existing top-items chart uses.
  const rowChartHeight = (rows, min = 160) => Math.max(min, (rows || 0) * 34 + 40);

  const toggleServerSort = (column) => {
    setServerSort((prev) => (
      prev.column === column
        ? { column, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
        : { column, direction: column === 'serverName' ? 'asc' : 'desc' }
    ));
  };

  const formatServerCell = (row, col) => {
    const value = row?.[col.key];
    if (col.kind === 'text') return String(value ?? DASH);
    if (col.kind === 'money') return formatMoneyOrDash(value);
    if (col.kind === 'percent') return formatPercent(value);
    if (col.kind === 'hours') return formatHours(value);
    const n = Number(value);
    return Number.isFinite(n) ? n.toLocaleString() : DASH;
  };

  const exportStoreOrders = () => {
    const rows = [
      { metric: 'Store orders', value: storeStats.orders },
      { metric: 'Store revenue', value: formatMoneyFromCents(storeStats.salesCents) },
      { metric: 'Dine-in orders', value: storeStats.dineIn.orders },
      { metric: 'Dine-in revenue', value: formatMoneyFromCents(storeStats.dineIn.salesCents) },
      { metric: 'Dine-in avg order', value: formatMoneyOrDash(storeStats.dineIn.avgOrderValueCents) },
      { metric: 'To-go orders', value: storeStats.toGo.orders },
      { metric: 'To-go revenue', value: formatMoneyFromCents(storeStats.toGo.salesCents) },
      { metric: 'To-go avg order', value: formatMoneyOrDash(storeStats.toGo.avgOrderValueCents) },
      { metric: 'Covers', value: storeStats.covers },
      { metric: 'Dine-in orders with no guest count', value: storeStats.dineInWithoutCovers },
      { metric: 'Spend per cover', value: formatMoneyOrDash(storeStats.spendPerCoverCents) },
      { metric: 'Upsell rate', value: formatPercent(storeStats.upsellRate, 1) },
      { metric: 'Value added by additions', value: formatMoneyFromCents(storeStats.valueAddedCents) },
      { metric: 'Void rate', value: formatPercent(storeStats.voidRate, 1) },
      { metric: 'Value removed by edits', value: formatMoneyFromCents(storeStats.valueRemovedCents) },
      { metric: 'Unpaid store tickets', value: storeStats.unpaid.orders },
      { metric: 'Unpaid value', value: formatMoneyFromCents(storeStats.unpaid.salesCents) },
      { metric: 'Cancelled store orders', value: storeStats.cancelled.orders },
      { metric: 'Cancelled store value (excluded from revenue)', value: formatMoneyFromCents(storeStats.cancelled.salesCents) },
    ];
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv({ filename: `sales-store-summary-${stamp}.csv`, rows });
  };

  const exportServerLeaderboard = () => {
    const rows = sortedServers.map((s) => ({
      server: s.serverName,
      server_id: s.serverId,
      orders: s.orders,
      revenue: formatMoneyFromCents(s.salesCents),
      avg_order: formatMoneyOrDash(s.avgOrderValueCents),
      covers: s.covers,
      spend_per_cover: formatMoneyOrDash(s.spendPerCoverCents),
      upsell_rate: formatPercent(s.upsellRate, 1),
      value_added: formatMoneyFromCents(s.valueAddedCents),
      revenue_per_active_hour: formatMoneyOrDash(s.revenuePerActiveHourCents),
      void_rate: formatPercent(s.voidRate, 1),
      value_removed: formatMoneyFromCents(s.valueRemovedCents),
      active_hours_proxy: formatHours(s.activeHours),
      top_items: s.topItems.map((it) => `${it.name} ×${it.quantity}`).join(' | '),
    }));
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv({ filename: `sales-servers-${stamp}.csv`, rows });
  };

  const exportRawOrders = () => {
    const rows = filteredOrders.map((o) => {
      const cents = o?.amount_total_cents ?? o?.amount_total;
      const n = Number(cents);
      const revenue = Number.isFinite(n) ? Math.floor(n) : 0;
      return {
        order_number: o?.order_number || '',
        order_timestamp: o?.order_timestamp || '',
        paid_at: o?.paid_at || '',
        channel: normalizeChannel(o),
        pay_type: normalizePayType(o) === 'pay_at_store' ? 'PAY_AT_STORE' : 'PAY_ONLINE',
        payment_status: String(o?.displayPaymentStatus || o?.payment_status || o?.paymentStatus || '').trim(),
        status: String(o?.displayStatus || o?.status || '').trim(),
        total_cents: revenue,
        total: formatMoneyFromCents(revenue),
        items_count: safeArray(o?.items).length,
      };
    });

    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv({ filename: `sales-raw-orders-${stamp}.csv`, rows });
  };

  const exportTopItems = () => {
    const rows = topItems.map((it) => ({
      product_id: it.productId || '',
      name: it.name || it.key,
      quantity: it.quantity,
      revenue_cents: it.revenueCents,
      revenue: formatMoneyFromCents(it.revenueCents),
      category: it.category || '',
      meals: it.meals || '',
    }));
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv({ filename: `sales-top-items-${stamp}.csv`, rows });
  };

  const exportSeries = () => {
    const rows = trendSeries.map((r) => ({
      bucket: r.bucket,
      pay_online_revenue: r.payOnlineRevenue,
      pay_at_store_revenue: r.payAtStoreRevenue,
      orders: r.orders,
      total_revenue: r.revenue,
    }));
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv({ filename: `sales-series-${stamp}.csv`, rows });
  };

  const COLORS = {
    pay_online: '#22c55e',
    pay_at_store: '#fb923c',
    delivery: '#3b82f6',
    pickup: '#8b5cf6',
  };

  const TOP_ITEM_COLORS = [
    '#7c3aed',
    '#2563eb',
    '#0891b2',
    '#16a34a',
    '#ea580c',
    '#db2777',
    '#4f46e5',
    '#0f766e',
    '#65a30d',
    '#c2410c',
    '#be185d',
    '#6d28d9',
  ];

  const payOnlineLabel = useMemo(() => formatMoneyFromCents(paySplit.pay_online.sales_cents), [paySplit]);
  const payAtStoreLabel = useMemo(() => formatMoneyFromCents(paySplit.pay_at_store.sales_cents), [paySplit]);
  const deliveryLabel = useMemo(() => formatMoneyFromCents(fulfillmentSplit.delivery.sales_cents), [fulfillmentSplit]);
  const pickupLabel = useMemo(() => formatMoneyFromCents(fulfillmentSplit.pickup.sales_cents), [fulfillmentSplit]);

  const filterRowStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 14,
    alignItems: 'end',
  };

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        const access = await resolveTabAccess(session, 'Orders/Reports');
        if (!access.allowed) {
          navigate(access.isPosAdmin ? '/admin/channels/pos/queue' : '/admin/login');
          return;
        }

        setUsername(user.username || user.attributes?.email || 'Admin');
        
        // Load locations
        try {
          if (API_BASE_URL) {
            const token = session.getIdToken().getJwtToken();
            const res = await fetch(`${API_BASE_URL}/admin/locations`, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
              const list = Array.isArray(data?.locations) ? data.locations : [];
              setLocations(list);
            }
          }
        } catch (e) {
          console.error('Failed to load locations:', e);
        }
      } catch {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      navigate('/admin/login');
    } catch {
      // ignore
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-header-logo">
            <i className="fas fa-chart-line"></i>
            <span>{PROJECT_NAME}</span>
          </div>
          <div className="admin-header-welcome">
            <h1>Sales Report</h1>
            <p>Revenue + payment insights across store, online and marketplace channels</p>
          </div>
        </div>

        <div className="admin-header-right">
          <AdminNavTabs />
          <span className="admin-user">
            <i className="fas fa-user-shield"></i>
            {username}
          </span>
          <button onClick={handleLogout} className="btn-logout">
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      <nav className="reports-subtabs" aria-label="Reports navigation">
        <NavLink to="/admin/reports/orders" className={({ isActive }) => `reports-subtab${isActive ? ' active' : ''}`}>
          Orders
        </NavLink>
        {/* Route path stays /admin/reports/online-sales so existing bookmarks keep
            resolving (R2.3); only the label changed. */}
        <NavLink to="/admin/reports/online-sales" className={({ isActive }) => `reports-subtab${isActive ? ' active' : ''}`}>
          Sales
        </NavLink>
        <NavLink to="/admin/reports/fulfillment" className={({ isActive }) => `reports-subtab${isActive ? ' active' : ''}`}>
          Fulfillment
        </NavLink>
      </nav>

      <div className="admin-content">
        {statusMessage ? (
          <div className="alert alert-success" ref={statusRef} tabIndex={-1} role="status" aria-live="polite">
            <i className="fas fa-check-circle"></i>
            {statusMessage}
          </div>
        ) : null}

        {error ? (
          <div className="alert alert-error" ref={statusRef} tabIndex={-1} role="alert" aria-live="assertive">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        ) : null}

        <div className="product-form-container">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <h2>Report Filters</h2>
              <p style={{ marginTop: 6, color: 'rgba(15, 23, 42, 0.65)' }}>
                Sales across all channels, with Pay Online vs Pay at Store split.
              </p>
            </div>

            <div className="report-actions">
              <button className="admin-action-btn" onClick={runReport} disabled={searching}>
                {searching ? 'Loading…' : 'Run report'}
              </button>
              <button className="admin-action-btn" onClick={exportRawOrders} disabled={filteredOrders.length === 0}>
                Export orders CSV
              </button>
              <button className="admin-action-btn" onClick={exportTopItems} disabled={topItems.length === 0}>
                Export top items CSV
              </button>
              <button className="admin-action-btn" onClick={exportSeries} disabled={trendSeries.length === 0}>
                Export series CSV
              </button>
            </div>
          </div>

          <div className="form-row" style={filterRowStyle}>
            <div className="form-group">
              <label>Range mode</label>
              <select value={rangeMode} onChange={(e) => setRangeMode(e.target.value === 'custom' ? 'custom' : 'lookback')}>
                <option value="lookback">Lookback</option>
                <option value="custom">Custom dates</option>
              </select>
            </div>

            {rangeMode === 'lookback' ? (
              <>
                <div className="form-group">
                  <label>Lookback</label>
                  <input
                    type="number"
                    min="1"
                    max={lookbackUnit === 'hours' ? '720' : '365'}
                    value={lookbackValue}
                    onChange={(e) => setLookbackValue(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Unit</label>
                  <select value={lookbackUnit} onChange={(e) => setLookbackUnit(e.target.value === 'hours' ? 'hours' : 'days')}>
                    <option value="days">Days</option>
                    <option value="hours">Hours</option>
                  </select>
                </div>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label>Start</label>
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>End</label>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </div>
              </>
            )}
          </div>

          <div className="form-row" style={filterRowStyle}>
            <div className="form-group">
              <label>Status</label>
              <select value={orderStatusFilter} onChange={(e) => setOrderStatusFilter(e.target.value)}>
                {statusOptions.map((opt) => (
                  <option key={`status-${opt.k}`} value={opt.k}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Location</label>
              <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
                <option value="ALL">ALL LOCATIONS</option>
                {locations.map((loc) => (
                  <option key={loc.location_id} value={loc.location_id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Channel</label>
              <select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)}>
                {channels.map((c) => (
                  <option key={`channel-${c}`} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Sales channel</label>
              <select value={dimFilter} onChange={(e) => setDimFilter(e.target.value)}>
                <option value="ALL">ALL</option>
                <option value={STORE}>{CHANNEL_LABELS[STORE]}</option>
                <option value={ONLINE}>{CHANNEL_LABELS[ONLINE]}</option>
                <option value={MARKETPLACE}>{CHANNEL_LABELS[MARKETPLACE]}</option>
              </select>
            </div>

            <div className="form-group">
              <label>Payment type</label>
              <select value={payTypeFilter} onChange={(e) => setPayTypeFilter(e.target.value)}>
                <option value="ALL">ALL</option>
                <option value="PAY_ONLINE">Pay online</option>
                <option value="PAY_AT_STORE">Pay at store</option>
              </select>
            </div>

            <div className="form-group">
              <label>Meal</label>
              <select value={mealFilter} onChange={(e) => setMealFilter(e.target.value)}>
                {availableMeals.map((m) => (
                  <option key={`meal-${m}`} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Category</label>
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                {availableCategories.map((c) => (
                  <option key={`cat-${c}`} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Trend bucket</label>
              <select value={granularity} onChange={(e) => setGranularity(e.target.value)}>
                <option value="auto">Auto</option>
                <option value="day">Day</option>
                <option value="hour">Hour</option>
              </select>
            </div>
          </div>
        </div>

        <section className="fulfillment-kpi-grid">
          <div className="fulfillment-kpi-card">
            <div className="fulfillment-kpi-title">Total sales</div>
            <div className="fulfillment-kpi-value">{formatMoneyFromCents(totalSalesCents)}</div>
            <div className="fulfillment-kpi-sub">{totalOrders.toLocaleString()} order(s)</div>
          </div>
          <div className="fulfillment-kpi-card">
            <div className="fulfillment-kpi-title">Total orders</div>
            <div className="fulfillment-kpi-value">{totalOrders.toLocaleString()}</div>
            <div className="fulfillment-kpi-sub">Filtered view</div>
          </div>
          <div className="fulfillment-kpi-card">
            <div className="fulfillment-kpi-title">Avg order value</div>
            <div className="fulfillment-kpi-value">{formatMoneyFromCents(avgOrderValueCents)}</div>
            <div className="fulfillment-kpi-sub">Sales / Orders</div>
          </div>
          <div className="fulfillment-kpi-card">
            <div className="fulfillment-kpi-title">Pending pay-at-store</div>
            {/* PRE-EXISTING BUG, fixed: `unpaidOrders` is an ARRAY, and
                Number([someOrder]) is NaN — so this card read "NaN" whenever there
                was at least one pending pay-at-store order, and "0" only when there
                were none. Reading `.length` is the count the label promises. */}
            <div className="fulfillment-kpi-value">{safeArray(unpaidOrders).length.toLocaleString()}</div>
            <div className="fulfillment-kpi-sub">Pay at store + pending payment</div>
          </div>
        </section>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 18 }}>
          <div className="fulfillment-chart-card">
            <div className="fulfillment-chart-header">
              <h3>Payment split</h3>
              <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span
                  className="status-pill"
                  style={{
                    background: `${COLORS.pay_online}1a`,
                    border: `1px solid ${COLORS.pay_online}66`,
                    color: '#0f172a',
                  }}
                >
                  {payOnlineLabel} online
                </span>
                <span
                  className="status-pill"
                  style={{
                    background: `${COLORS.pay_at_store}1a`,
                    border: `1px solid ${COLORS.pay_at_store}66`,
                    color: '#0f172a',
                  }}
                >
                  {payAtStoreLabel} at store
                </span>
              </div>
            </div>
            <div className="fulfillment-chart-body">
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Tooltip
                      formatter={(v) => formatMoneyFromCents(v)}
                      contentStyle={{ borderRadius: 12 }}
                    />
                    <Legend />
                    <Pie data={paymentDonutData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={92} paddingAngle={3}>
                      {paymentDonutData.map((entry) => (
                        <Cell key={entry.key} fill={COLORS[entry.key] || '#94a3b8'} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="fulfillment-chart-card">
            <div className="fulfillment-chart-header">
              <h3>Fulfillment split</h3>
              <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span
                  className="status-pill"
                  style={{
                    background: `${COLORS.delivery}1a`,
                    border: `1px solid ${COLORS.delivery}66`,
                    color: '#0f172a',
                  }}
                >
                  {deliveryLabel} delivery
                </span>
                <span
                  className="status-pill"
                  style={{
                    background: `${COLORS.pickup}1a`,
                    border: `1px solid ${COLORS.pickup}66`,
                    color: '#0f172a',
                  }}
                >
                  {pickupLabel} pickup
                </span>
              </div>
            </div>
            <div className="fulfillment-chart-body">
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Tooltip
                      formatter={(v) => formatMoneyFromCents(v)}
                      contentStyle={{ borderRadius: 12 }}
                    />
                    <Legend />
                    <Pie data={fulfillmentDonutData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={92} paddingAngle={3}>
                      {fulfillmentDonutData.map((entry) => (
                        <Cell key={entry.key} fill={COLORS[entry.key] || '#94a3b8'} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="fulfillment-chart-card">
            <div className="fulfillment-chart-header">
              <h3>Fulfillment trend</h3>
              <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span className="status-pill">Bucket: {resolvedGranularity}</span>
                <span
                  className="status-pill"
                  style={{
                    background: `${COLORS.delivery}1a`,
                    border: `1px solid ${COLORS.delivery}66`,
                    color: '#0f172a',
                  }}
                >
                  {deliveryLabel}
                </span>
                <span
                  className="status-pill"
                  style={{
                    background: `${COLORS.pickup}1a`,
                    border: `1px solid ${COLORS.pickup}66`,
                    color: '#0f172a',
                  }}
                >
                  {pickupLabel}
                </span>
              </div>
            </div>
            <div className="fulfillment-chart-body">
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <ComposedChart data={trendSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}`} />
                    <Tooltip formatter={(v) => `${Number(v).toFixed(2)}`} />
                    <Legend />
                    <Line type="monotone" dataKey="deliveryRevenue" name="Delivery" stroke={COLORS.delivery} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="pickupRevenue" name="Pickup" stroke={COLORS.pickup} strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="fulfillment-chart-card">
            <div className="fulfillment-chart-header">
              <h3>Sales trend</h3>
              <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span className="status-pill">Bucket: {resolvedGranularity}</span>
                <span
                  className="status-pill"
                  style={{
                    background: `${COLORS.pay_online}1a`,
                    border: `1px solid ${COLORS.pay_online}66`,
                    color: '#0f172a',
                  }}
                >
                  {payOnlineLabel}
                </span>
                <span
                  className="status-pill"
                  style={{
                    background: `${COLORS.pay_at_store}1a`,
                    border: `1px solid ${COLORS.pay_at_store}66`,
                    color: '#0f172a',
                  }}
                >
                  {payAtStoreLabel}
                </span>
              </div>
            </div>
            <div className="fulfillment-chart-body">
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <ComposedChart data={trendSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${currencySymbol()}${v}`} />
                    <Tooltip formatter={(v) => formatMajor(Number(v))} />
                    <Legend />
                    <Line type="monotone" dataKey="payOnlineRevenue" name="Pay Online" stroke={COLORS.pay_online} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="payAtStoreRevenue" name="Pay at Store" stroke={COLORS.pay_at_store} strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="fulfillment-chart-card">
            <div className="fulfillment-chart-header">
              <h3>Orders vs revenue</h3>
              <span className="status-pill">{trendSeries.length} bucket(s)</span>
            </div>
            <div className="fulfillment-chart-body">
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <ComposedChart data={comboSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} tickFormatter={(v) => `${currencySymbol()}${v}`} />
                    <Tooltip formatter={(v, name) => (name === 'Revenue' ? formatMajor(Number(v)) : v)} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="orders" name="Orders" fill="#60a5fa" radius={[10, 10, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue" stroke="#7c3aed" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16, marginBottom: 24 }}>
          <div className="fulfillment-chart-card">
            <div className="fulfillment-chart-header">
              <h3>Top selling items</h3>
              <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  className="admin-mini-btn"
                  onClick={() => setTopMode('revenue')}
                  style={{ opacity: topMode === 'revenue' ? 1 : 0.7 }}
                >
                  By revenue
                </button>
                <button
                  className="admin-mini-btn"
                  onClick={() => setTopMode('quantity')}
                  style={{ opacity: topMode === 'quantity' ? 1 : 0.7 }}
                >
                  By qty
                </button>
              </div>
            </div>

            <div className="fulfillment-chart-body">
              <div style={{ width: '100%', height: topItemsChartHeightForDisplay }}>
                <ResponsiveContainer>
                  <BarChart data={topItemsChartDataForDisplay} layout="vertical" margin={{ left: 20, right: 18 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis type="category" dataKey="label" width={170} tick={{ fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{ borderRadius: 12 }}
                      formatter={(v, name, p) => {
                        const n = Number(v);
                        const label = topMode === 'quantity' ? 'Qty' : 'Revenue';
                        const val = topMode === 'quantity' ? (Number.isFinite(n) ? n : 0) : formatMajor(Number.isFinite(n) ? n : 0);
                        return [val, label];
                      }}
                      labelFormatter={(label, payload) => {
                        const p = Array.isArray(payload) && payload.length ? payload[0]?.payload : null;
                        return p?.fullName || label;
                      }}
                    />
                    <Bar
                      dataKey={topMode === 'quantity' ? 'quantity' : 'revenue'}
                      name={topMode === 'quantity' ? 'Quantity' : 'Revenue'}
                      radius={[0, 10, 10, 0]}
                      isAnimationActive={false}
                    >
                      {topItemsChartDataForDisplay.map((entry, idx) => (
                        <Cell key={`cell-${entry.rank || idx}`} fill={TOP_ITEM_COLORS[idx % TOP_ITEM_COLORS.length]} />
                      ))}
                      <LabelList
                        dataKey={topMode === 'quantity' ? 'quantity' : 'revenue'}
                        position="right"
                        formatter={(v) => {
                          const n = Number(v);
                          if (!Number.isFinite(n)) return '';
                          return topMode === 'quantity' ? `${Math.round(n)}` : formatMajor(n);
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="fulfillment-chart-card">
            <div className="fulfillment-chart-header">
              <h3>Insights</h3>
              <span className="status-pill info">Auto</span>
            </div>
            <div className="fulfillment-chart-body">
              <div style={{ display: 'grid', gap: 10 }}>
                <div className="status-pill">
                  Pay online: {formatMoneyFromCents(paySplit.pay_online.sales_cents)}
                </div>
                <div className="status-pill warning">
                  Pay at store: {formatMoneyFromCents(paySplit.pay_at_store.sales_cents)}
                </div>
                <div className="status-pill">
                  Pay at store share:{' '}
                  {totalSalesCents > 0
                    ? `${Math.round((paySplit.pay_at_store.sales_cents / totalSalesCents) * 100)}%`
                    : '0%'}
                </div>
                <div style={{ color: 'rgba(15, 23, 42, 0.7)', fontSize: '0.92rem', lineHeight: 1.4 }}>
                  Use meal/category filters to isolate what drives pay-at-store volume. Pending pay-at-store is a quick proxy for
                  unpaid/no-show risk.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ---------------------------------------------------------------
            Sales channel dimension (R3). Store means orderSource === 'staff'
            — an order taken through Take Order. Everything below excludes
            cancelled orders and reports them separately, which is why these
            figures can read lower than "Total sales" above when the range
            contains cancellations.
            --------------------------------------------------------------- */}
        <section className="fulfillment-chart-card" style={{ marginBottom: 18 }}>
          <div className="fulfillment-chart-header">
            <h3>Sales by channel</h3>
            <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {channelRows.map((row) => (
                <span
                  key={`chan-pill-${row.key}`}
                  className="status-pill"
                  style={{
                    background: `${CHANNEL_COLORS[row.key]}1a`,
                    border: `1px solid ${CHANNEL_COLORS[row.key]}66`,
                    color: '#0f172a',
                  }}
                >
                  {row.name}: {formatMoneyFromCents(row.value)} ({formatPercent(row.share)})
                </span>
              ))}
            </div>
          </div>
          <div className="fulfillment-chart-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 14 }}>
              <div style={{ overflowX: 'auto' }}>
              <table className="admin-table" style={{ width: '100%', marginBottom: 14 }}>
                <thead>
                  <tr>
                    <th>Channel</th>
                    <th style={{ textAlign: 'right' }}>Orders</th>
                    <th style={{ textAlign: 'right' }}>Revenue</th>
                    <th style={{ textAlign: 'right' }}>Share</th>
                    <th style={{ textAlign: 'right' }}>Avg order</th>
                  </tr>
                </thead>
                <tbody>
                  {channelRows.map((row) => (
                    <tr key={`chan-row-${row.key}`}>
                      <td>{row.name}</td>
                      <td style={{ textAlign: 'right' }}>{row.orders.toLocaleString()}</td>
                      <td style={{ textAlign: 'right' }}>{formatMoneyFromCents(row.value)}</td>
                      <td style={{ textAlign: 'right' }}>{formatPercent(row.share)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {row.orders ? formatMoneyFromCents(Math.round(row.value / row.orders)) : DASH}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: 600 }}>
                    <td>Total (excl. cancelled)</td>
                    <td style={{ textAlign: 'right' }}>{channelStats.totalOrders.toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }}>{formatMoneyFromCents(channelStats.totalSalesCents)}</td>
                    <td style={{ textAlign: 'right' }}>100%</td>
                    <td style={{ textAlign: 'right' }}>
                      {channelStats.totalOrders
                        ? formatMoneyFromCents(Math.round(channelStats.totalSalesCents / channelStats.totalOrders))
                        : DASH}
                    </td>
                  </tr>
                  {channelStats.cancelled.orders > 0 ? (
                    <tr style={{ color: 'rgba(15, 23, 42, 0.65)' }}>
                      <td>Cancelled (excluded above)</td>
                      <td style={{ textAlign: 'right' }}>{channelStats.cancelled.orders.toLocaleString()}</td>
                      <td style={{ textAlign: 'right' }}>{formatMoneyFromCents(channelStats.cancelled.salesCents)}</td>
                      <td style={{ textAlign: 'right' }}>{DASH}</td>
                      <td style={{ textAlign: 'right' }}>{DASH}</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
              </div>

              <div>
                {channelDonutData.length === 0 ? (
                  <div style={{ color: 'rgba(15, 23, 42, 0.7)' }}>No revenue in this range.</div>
                ) : (
                  <div style={{ width: '100%', height: 260 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Tooltip formatter={(v) => formatMajor(Number(v) || 0)} contentStyle={{ borderRadius: 12 }} />
                        <Legend />
                        <Pie data={channelDonutData} dataKey="value" nameKey="name" innerRadius={58} outerRadius={90} paddingAngle={3}>
                          {channelDonutData.map((entry) => (
                            <Cell key={`cd-${entry.key}`} fill={CHANNEL_COLORS[entry.key] || '#94a3b8'} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>

            <div>
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <ComposedChart data={channelTrend} margin={{ left: 8, right: 18 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatMajor(Number(v) || 0)} />
                    <Tooltip contentStyle={{ borderRadius: 12 }} formatter={(v) => formatMajor(Number(v) || 0)} />
                    <Legend />
                    <Bar dataKey={STORE} name={CHANNEL_LABELS[STORE]} stackId="ch" fill={CHANNEL_COLORS[STORE]} isAnimationActive={false} />
                    <Bar dataKey={ONLINE} name={CHANNEL_LABELS[ONLINE]} stackId="ch" fill={CHANNEL_COLORS[ONLINE]} isAnimationActive={false} />
                    <Bar dataKey={MARKETPLACE} name={CHANNEL_LABELS[MARKETPLACE]} stackId="ch" fill={CHANNEL_COLORS[MARKETPLACE]} isAnimationActive={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </section>

        {/* ---------------------------------------------------------------
            Settlement summary (cash-payment-reporting): how the period's
            revenue was PAID and how it was FULFILLED — the end-of-day
            reconciliation view. Cancelled excluded; unpaid shown in the
            payment split but NOT added to Collected, so the collected total
            is honest.
            --------------------------------------------------------------- */}
        <section className="fulfillment-chart-card" style={{ marginBottom: 18 }}>
          <div className="fulfillment-chart-header">
            <h3>Payment &amp; fulfillment summary</h3>
            <span className="status-pill" style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#166534' }}>
              Collected: {formatMoneyFromCents(settlement.settledCents)} · {settlement.settledOrders.toLocaleString()} order(s)
            </span>
          </div>

          {/* Two independent cards. `minmax(0, 1fr)` (not the previous
              `minmax(320px, 1fr)` with auto-fit) is the fix for the overlapping
              columns: auto-fit + a 320px minimum let each track refuse to shrink and
              the tables bled into each other on a narrow container. A 0-minimum track
              can actually shrink, each column is a bordered card so they are visually
              separated, and the grid collapses to one column under 720px. */}
          <div className="settlement-split-grid" style={{ display: 'grid', gap: 18, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', marginTop: 12 }}>
            {/* By payment type */}
            <div style={{ minWidth: 0, border: '1px solid rgba(15,23,42,0.08)', borderRadius: 12, padding: 14 }}>
              <h4 style={{ margin: '0 0 8px', color: '#0f172a' }}>By payment type</h4>

              {paymentSplitDonutData.length === 0 ? (
                <div style={{ color: 'rgba(15,23,42,0.6)', fontSize: '0.85rem', marginBottom: 8 }}>
                  No collected payments to chart in this range.
                </div>
              ) : (
                <div style={{ width: '100%', height: 220 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Tooltip formatter={(v) => formatMajor(Number(v) || 0)} contentStyle={{ borderRadius: 12 }} />
                      <Legend />
                      <Pie data={paymentSplitDonutData} dataKey="value" nameKey="name" innerRadius={52} outerRadius={82} paddingAngle={3}>
                        {paymentSplitDonutData.map((entry) => (
                          <Cell key={`pd-${entry.key}`} fill={PAYMENT_TYPE_COLORS[entry.key] || '#94a3b8'} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}

              <table className="fulfillment-table">
                <thead>
                  <tr>
                    <th>Payment type</th>
                    <th style={{ textAlign: 'right' }}>Orders</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {settlement.byPayment.length === 0 ? (
                    <tr><td colSpan={3} style={{ color: 'rgba(15,23,42,0.6)' }}>No orders in this range.</td></tr>
                  ) : (
                    settlement.byPayment.map((row) => (
                      <tr key={`pay-${row.key}`} style={row.key === 'UNPAID' ? { color: 'rgba(15,23,42,0.6)' } : null}>
                        <td>{paymentTypeLabel(row.key)}</td>
                        <td style={{ textAlign: 'right' }}>{row.orders.toLocaleString()}</td>
                        <td style={{ textAlign: 'right' }}>{formatMoneyFromCents(row.salesCents)}</td>
                      </tr>
                    ))
                  )}
                  <tr style={{ fontWeight: 600 }}>
                    <td>Collected (excl. unpaid &amp; cancelled)</td>
                    <td style={{ textAlign: 'right' }}>{settlement.settledOrders.toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }}>{formatMoneyFromCents(settlement.settledCents)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* By fulfillment */}
            <div style={{ minWidth: 0, border: '1px solid rgba(15,23,42,0.08)', borderRadius: 12, padding: 14 }}>
              <h4 style={{ margin: '0 0 8px', color: '#0f172a' }}>By fulfillment</h4>

              {fulfillmentSplitDonutData.length === 0 ? (
                <div style={{ color: 'rgba(15,23,42,0.6)', fontSize: '0.85rem', marginBottom: 8 }}>
                  No fulfilled revenue to chart in this range.
                </div>
              ) : (
                <div style={{ width: '100%', height: 220 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Tooltip formatter={(v) => formatMajor(Number(v) || 0)} contentStyle={{ borderRadius: 12 }} />
                      <Legend />
                      <Pie data={fulfillmentSplitDonutData} dataKey="value" nameKey="name" innerRadius={52} outerRadius={82} paddingAngle={3}>
                        {fulfillmentSplitDonutData.map((entry, idx) => (
                          <Cell key={`fd-${entry.key}`} fill={FULFILLMENT_PALETTE[idx % FULFILLMENT_PALETTE.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}

              <table className="fulfillment-table">
                <thead>
                  <tr>
                    <th>Fulfillment</th>
                    <th style={{ textAlign: 'right' }}>Orders</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {settlement.byFulfillment.length === 0 ? (
                    <tr><td colSpan={3} style={{ color: 'rgba(15,23,42,0.6)' }}>No orders in this range.</td></tr>
                  ) : (
                    settlement.byFulfillment.map((row) => (
                      <tr key={`ful-${row.key}`}>
                        <td>{row.key}</td>
                        <td style={{ textAlign: 'right' }}>{row.orders.toLocaleString()}</td>
                        <td style={{ textAlign: 'right' }}>{formatMoneyFromCents(row.salesCents)}</td>
                      </tr>
                    ))
                  )}
                  {settlement.cancelled.orders > 0 ? (
                    <tr style={{ color: 'rgba(15,23,42,0.65)' }}>
                      <td>Cancelled (excluded)</td>
                      <td style={{ textAlign: 'right' }}>{settlement.cancelled.orders.toLocaleString()}</td>
                      <td style={{ textAlign: 'right' }}>{formatMoneyFromCents(settlement.cancelled.salesCents)}</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ---------------------------------------------------------------
            Store orders (R4). Explicit empty state rather than a grid of
            zeros, which would read as "we sold nothing in store" instead of
            "this range has no store orders".
            --------------------------------------------------------------- */}
        <section className="fulfillment-chart-card" style={{ marginBottom: 18 }}>
          <div className="fulfillment-chart-header">
            <h3>Store orders (Take Order)</h3>
            <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span className="status-pill info">{storeStats.orders.toLocaleString()} order(s)</span>
              <button className="admin-mini-btn" onClick={exportStoreOrders} disabled={!hasStoreOrders}>
                Export store CSV
              </button>
            </div>
          </div>

          <div className="fulfillment-chart-body">
            {!hasStoreOrders ? (
              <div style={{ color: 'rgba(15, 23, 42, 0.7)', lineHeight: 1.5 }}>
                No store orders in this range. Orders taken through <strong>Take Order</strong> appear here; online and
                marketplace orders are reported in the sections above.
              </div>
            ) : (
              <>
                <section className="fulfillment-kpi-grid" style={{ marginBottom: 16 }}>
                  <div className="fulfillment-kpi-card">
                    <div className="fulfillment-kpi-title">Store revenue</div>
                    <div className="fulfillment-kpi-value">{formatMoneyFromCents(storeStats.salesCents)}</div>
                    <div className="fulfillment-kpi-sub">{storeStats.orders.toLocaleString()} order(s), cancelled excluded</div>
                  </div>
                  <div className="fulfillment-kpi-card">
                    <div className="fulfillment-kpi-title">Dine-in / to-go</div>
                    <div className="fulfillment-kpi-value">
                      {storeStats.dineIn.orders.toLocaleString()} / {storeStats.toGo.orders.toLocaleString()}
                    </div>
                    <div className="fulfillment-kpi-sub">
                      {formatMoneyFromCents(storeStats.dineIn.salesCents)} / {formatMoneyFromCents(storeStats.toGo.salesCents)}
                    </div>
                  </div>
                  <div className="fulfillment-kpi-card">
                    <div className="fulfillment-kpi-title">Spend per cover</div>
                    <div className="fulfillment-kpi-value">{formatMoneyOrDash(storeStats.spendPerCoverCents)}</div>
                    <div className="fulfillment-kpi-sub">
                      {storeStats.covers.toLocaleString()} cover(s)
                      {storeStats.dineInWithoutCovers > 0
                        ? ` · ${storeStats.dineInWithoutCovers} dine-in order(s) with no guest count excluded`
                        : ''}
                    </div>
                  </div>
                  <div className="fulfillment-kpi-card">
                    <div className="fulfillment-kpi-title">Unpaid tickets</div>
                    <div className="fulfillment-kpi-value">{storeStats.unpaid.orders.toLocaleString()}</div>
                    <div className="fulfillment-kpi-sub">
                      {formatMoneyFromCents(storeStats.unpaid.salesCents)} open · oldest {formatAge(storeStats.unpaid.oldestAgeHours)}
                    </div>
                  </div>
                  <div className="fulfillment-kpi-card">
                    <div className="fulfillment-kpi-title">Upsell rate</div>
                    <div className="fulfillment-kpi-value">{formatPercent(storeStats.upsellRate, 1)}</div>
                    <div className="fulfillment-kpi-sub">
                      {storeStats.upsoldOrders.toLocaleString()} ticket(s) added to · {formatMoneyFromCents(storeStats.valueAddedCents)} added
                    </div>
                  </div>
                  <div className="fulfillment-kpi-card">
                    <div className="fulfillment-kpi-title">Void rate</div>
                    <div className="fulfillment-kpi-value">{formatPercent(storeStats.voidRate, 1)}</div>
                    <div className="fulfillment-kpi-sub">
                      {storeStats.voidedOrders.toLocaleString()} ticket(s) edited · {formatMoneyFromCents(storeStats.valueRemovedCents)} removed
                    </div>
                  </div>
                  <div className="fulfillment-kpi-card">
                    <div className="fulfillment-kpi-title">Cancelled store orders</div>
                    <div className="fulfillment-kpi-value">{storeStats.cancelled.orders.toLocaleString()}</div>
                    <div className="fulfillment-kpi-sub">
                      {formatMoneyFromCents(storeStats.cancelled.salesCents)} — excluded from every revenue figure
                    </div>
                  </div>
                </section>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
                  <div>
                    <h4 style={{ margin: '0 0 8px' }}>Orders by hour of day</h4>
                    <div style={{ width: '100%', height: 220 }}>
                      <ResponsiveContainer>
                        <BarChart data={storeStats.byHour} margin={{ left: 4, right: 12 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={1} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                          <Tooltip contentStyle={{ borderRadius: 12 }} />
                          <Bar dataKey="orders" name="Orders" fill={CHANNEL_COLORS[STORE]} isAnimationActive={false} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div style={{ color: 'rgba(15, 23, 42, 0.6)', fontSize: '0.82rem' }}>
                      Bucketed in this browser's timezone.
                    </div>
                  </div>

                  <div>
                    <h4 style={{ margin: '0 0 8px' }}>Orders by weekday</h4>
                    <div style={{ width: '100%', height: 220 }}>
                      <ResponsiveContainer>
                        <BarChart data={storeStats.byWeekday} margin={{ left: 4, right: 12 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                          <Tooltip contentStyle={{ borderRadius: 12 }} />
                          <Bar dataKey="orders" name="Orders" fill="#8b5cf6" isAnimationActive={false} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div>
                    <h4 style={{ margin: '0 0 8px' }}>Dine-in vs to-go revenue</h4>
                    {storeMixDonutData.length === 0 ? (
                      <div style={{ color: 'rgba(15, 23, 42, 0.7)' }}>No store revenue in this range.</div>
                    ) : (
                      <div style={{ width: '100%', height: 220 }}>
                        <ResponsiveContainer>
                          <PieChart>
                            <Tooltip formatter={(v) => formatMajor(Number(v) || 0)} contentStyle={{ borderRadius: 12 }} />
                            <Legend />
                            <Pie data={storeMixDonutData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={3}>
                              {storeMixDonutData.map((entry) => (
                                <Cell key={`mix-${entry.key}`} fill={STORE_MIX_COLORS[entry.key]} />
                              ))}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginTop: 16 }}>
                  <div style={{ overflowX: 'auto' }}>
                    <h4 style={{ margin: '0 0 8px' }}>Tables</h4>
                    {storeStats.tables.length === 0 ? (
                      <div style={{ color: 'rgba(15, 23, 42, 0.7)' }}>No dine-in tables in this range.</div>
                    ) : (
                      <>
                      <div style={{ width: '100%', height: rowChartHeight(tableChartData.length) }}>
                        <ResponsiveContainer>
                          <BarChart data={tableChartData} layout="vertical" margin={{ left: 8, right: 24 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => formatMajor(Number(v) || 0)} />
                            <YAxis type="category" dataKey="label" width={90} tick={{ fontSize: 11 }} />
                            <Tooltip
                              contentStyle={{ borderRadius: 12 }}
                              formatter={(v, name) => (name === 'Turns' ? Number(v) : formatMajor(Number(v) || 0))}
                            />
                            <Bar dataKey="revenue" name="Revenue" fill={CHANNEL_COLORS[STORE]} radius={[0, 8, 8, 0]} isAnimationActive={false}>
                              <LabelList dataKey="turns" position="right" formatter={(v) => `${v} turn(s)`} style={{ fontSize: 11 }} />
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <table className="admin-table" style={{ width: '100%' }}>
                        <thead>
                          <tr>
                            <th>Table</th>
                            <th style={{ textAlign: 'right' }}>Turns</th>
                            <th style={{ textAlign: 'right' }}>Revenue</th>
                            <th style={{ textAlign: 'right' }}>Avg order</th>
                          </tr>
                        </thead>
                        <tbody>
                          {storeStats.tables.map((t) => (
                            <tr key={`table-${t.tableLabel}`}>
                              <td>{t.tableLabel}</td>
                              <td style={{ textAlign: 'right' }}>{t.turns.toLocaleString()}</td>
                              <td style={{ textAlign: 'right' }}>{formatMoneyFromCents(t.salesCents)}</td>
                              <td style={{ textAlign: 'right' }}>{formatMoneyOrDash(t.avgOrderValueCents)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </>
                    )}
                  </div>

                  <div style={{ overflowX: 'auto' }}>
                    <h4 style={{ margin: '0 0 8px' }}>Top store items</h4>
                    {storeItems.length === 0 ? (
                      <div style={{ color: 'rgba(15, 23, 42, 0.7)' }}>No store items in this range.</div>
                    ) : (
                      <>
                      <div style={{ width: '100%', height: rowChartHeight(storeItemsChartData.length) }}>
                        <ResponsiveContainer>
                          <BarChart data={storeItemsChartData} layout="vertical" margin={{ left: 8, right: 28 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => formatMajor(Number(v) || 0)} />
                            <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 11 }} />
                            <Tooltip
                              contentStyle={{ borderRadius: 12 }}
                              formatter={(v, name) => (name === 'Qty' ? Number(v) : formatMajor(Number(v) || 0))}
                              labelFormatter={(label, payload) => {
                                const p = Array.isArray(payload) && payload.length ? payload[0]?.payload : null;
                                return p?.fullName || label;
                              }}
                            />
                            <Bar dataKey="revenue" name="Revenue" radius={[0, 8, 8, 0]} isAnimationActive={false}>
                              {storeItemsChartData.map((entry, idx) => (
                                <Cell key={`si-${entry.label}-${idx}`} fill={TOP_ITEM_COLORS[idx % TOP_ITEM_COLORS.length]} />
                              ))}
                              <LabelList dataKey="quantity" position="right" formatter={(v) => `×${v}`} style={{ fontSize: 11 }} />
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <table className="admin-table" style={{ width: '100%' }}>
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th style={{ textAlign: 'right' }}>Qty</th>
                            <th style={{ textAlign: 'right' }}>Revenue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {storeItems.map((it) => (
                            <tr key={`store-item-${it.key}`}>
                              <td>{it.name || it.key}</td>
                              <td style={{ textAlign: 'right' }}>{it.quantity.toLocaleString()}</td>
                              <td style={{ textAlign: 'right' }}>{formatMoneyFromCents(it.revenueCents)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </>
                    )}
                  </div>
                </div>

                {storeStats.unpaid.orders > 0 ? (
                  <div style={{ marginTop: 16 }}>
                    <h4 style={{ margin: '0 0 8px' }}>Unpaid store tickets by age</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
                      <div style={{ width: '100%', height: 220 }}>
                        <ResponsiveContainer>
                          <ComposedChart data={unpaidChartData} margin={{ left: 4, right: 12 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                            <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11 }} />
                            <YAxis
                              yAxisId="right"
                              orientation="right"
                              tick={{ fontSize: 11 }}
                              tickFormatter={(v) => `${currencySymbol()}${v}`}
                            />
                            <Tooltip
                              contentStyle={{ borderRadius: 12 }}
                              formatter={(v, name) => (name === 'Value' ? formatMajor(Number(v) || 0) : Number(v))}
                            />
                            <Legend />
                            {/* Ticket COUNT is the bar because that is the actionable number
                                (how many tabs are open); value rides the right axis as a line
                                so a single large tab does not visually dominate. */}
                            <Bar yAxisId="left" dataKey="tickets" name="Tickets" fill="#fb923c" radius={[8, 8, 0, 0]} isAnimationActive={false} />
                            <Line yAxisId="right" type="monotone" dataKey="value" name="Value" stroke="#0f766e" strokeWidth={2} dot isAnimationActive={false} />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>

                      <div style={{ overflowX: 'auto' }}>
                        <table className="admin-table" style={{ width: '100%' }}>
                          <thead>
                            <tr>
                              <th>Age</th>
                              <th style={{ textAlign: 'right' }}>Tickets</th>
                              <th style={{ textAlign: 'right' }}>Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {storeStats.unpaid.buckets.map((b) => (
                              <tr key={`unpaid-${b.label}`}>
                                <td>{b.label}</td>
                                <td style={{ textAlign: 'right' }}>{b.orders.toLocaleString()}</td>
                                <td style={{ textAlign: 'right' }}>{formatMoneyFromCents(b.salesCents)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div style={{ color: 'rgba(15, 23, 42, 0.6)', fontSize: '0.82rem', marginTop: 6 }}>
                          Oldest open ticket: {formatAge(storeStats.unpaid.oldestAgeHours)}
                          {storeStats.unpaid.tickets[0]?.orderNumber ? ` (#${storeStats.unpaid.tickets[0].orderNumber})` : ''}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </section>

        {/* ---------------------------------------------------------------
            Per-server report (R5, R6). Admin/Superadmin only — this is
            per-staff performance data and a waiter holding Orders/Reports
            must not see colleagues' numbers.
            --------------------------------------------------------------- */}
        {canSeeServerReport ? (
          <section className="fulfillment-chart-card" style={{ marginBottom: 24 }}>
            <div className="fulfillment-chart-header">
              <h3>Sales by server</h3>
              <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span className="status-pill info">Admin only</span>
                <button className="admin-mini-btn" onClick={exportServerLeaderboard} disabled={sortedServers.length === 0}>
                  Export servers CSV
                </button>
              </div>
            </div>

            <div className="fulfillment-chart-body">
              {sortedServers.length === 0 ? (
                <div style={{ color: 'rgba(15, 23, 42, 0.7)', lineHeight: 1.5 }}>
                  No store orders in this range, so there is nothing to attribute to a server.
                </div>
              ) : (
                <>
                  <div style={{ color: 'rgba(15, 23, 42, 0.72)', fontSize: '0.9rem', lineHeight: 1.5, marginBottom: 12 }}>
                    Revenue is credited entirely to the server who <strong>opened</strong> the ticket, even when a colleague added
                    to it later. Additions made to someone else's ticket are reported separately below.
                    {' '}The <strong>highlighted</strong> columns are the agreed perks basis: upsell rate and revenue per active
                    hour reward selling, and void rate sits beside them so the basis cannot be gamed by over-ordering and then
                    removing.
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 16 }}>
                    <div>
                      <h4 style={{ margin: '0 0 8px' }}>Revenue by server</h4>
                      {/* Three grouped bars per server, so the row allowance is tripled. */}
                      <div style={{ width: '100%', height: rowChartHeight(serverChartData.length * 3, 240) }}>
                        <ResponsiveContainer>
                          <BarChart data={serverChartData} layout="vertical" margin={{ left: 8, right: 24 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => formatMajor(Number(v) || 0)} />
                            <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 11 }} />
                            <Tooltip
                              contentStyle={{ borderRadius: 12 }}
                              formatter={(v, name) => (name === 'Orders' ? Number(v) : formatMajor(Number(v) || 0))}
                            />
                            <Legend />
                            {/* Value added and removed sit beside opener revenue rather than
                                inside it: the revenue bar is already the whole ticket total
                                under opener-takes-all, so stacking them would double-count. */}
                            <Bar dataKey="revenue" name="Revenue (opened)" fill={CHANNEL_COLORS[STORE]} radius={[0, 8, 8, 0]} isAnimationActive={false}>
                              <LabelList dataKey="orders" position="right" formatter={(v) => `${v} order(s)`} style={{ fontSize: 11 }} />
                            </Bar>
                            <Bar dataKey="valueAdded" name="Value added" fill="#22c55e" radius={[0, 8, 8, 0]} isAnimationActive={false} />
                            <Bar dataKey="valueRemoved" name="Value removed" fill="#ef4444" radius={[0, 8, 8, 0]} isAnimationActive={false} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div>
                      <h4 style={{ margin: '0 0 8px' }}>Perks basis ★</h4>
                      <div style={{ width: '100%', height: rowChartHeight(serverChartData.length * 3, 240) }}>
                        <ResponsiveContainer>
                          <ComposedChart data={serverChartData} margin={{ left: 4, right: 12 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                            {/* Two axes because the three perks metrics are not the same unit:
                                rates on the left as percentages, revenue per active hour on the
                                right as money. Plotting them on one axis would make whichever
                                number happens to be larger look like the important one. */}
                            <YAxis yAxisId="rate" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                            <YAxis
                              yAxisId="money"
                              orientation="right"
                              tick={{ fontSize: 11 }}
                              tickFormatter={(v) => `${currencySymbol()}${v}`}
                            />
                            <Tooltip
                              contentStyle={{ borderRadius: 12 }}
                              formatter={(v, name) => {
                                if (v == null) return [DASH, name];
                                return name === 'Revenue / active hour'
                                  ? [formatMajor(Number(v) || 0), name]
                                  : [`${Number(v)}%`, name];
                              }}
                            />
                            <Legend />
                            <Bar yAxisId="rate" dataKey="upsellPct" name="Upsell rate" fill="#0ea5e9" radius={[8, 8, 0, 0]} isAnimationActive={false} />
                            <Bar yAxisId="rate" dataKey="voidPct" name="Void rate" fill="#ef4444" radius={[8, 8, 0, 0]} isAnimationActive={false} />
                            <Line yAxisId="money" type="monotone" dataKey="perHour" name="Revenue / active hour" stroke="#7c3aed" strokeWidth={2} dot connectNulls={false} isAnimationActive={false} />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                      <div style={{ color: 'rgba(15, 23, 42, 0.6)', fontSize: '0.82rem' }}>
                        Void rate is shown next to the reward metrics on purpose, not as a
                        separate chart — the two are only meaningful read together.
                      </div>
                    </div>
                  </div>

                  <div style={{ overflowX: 'auto' }}>
                    <table className="admin-table" style={{ width: '100%' }}>
                      <thead>
                        <tr>
                          {SERVER_COLUMNS.map((col) => (
                            <th
                              key={`sc-${col.key}`}
                              onClick={() => toggleServerSort(col.key)}
                              style={{
                                cursor: 'pointer',
                                textAlign: col.kind === 'text' ? 'left' : 'right',
                                background: col.perks ? 'rgba(14, 165, 233, 0.12)' : undefined,
                                whiteSpace: 'nowrap',
                              }}
                              title={col.perks ? 'Perks basis metric' : 'Sort by this column'}
                            >
                              {col.label}
                              {col.perks ? ' ★' : ''}
                              {serverSort.column === col.key ? (serverSort.direction === 'asc' ? ' ▲' : ' ▼') : ''}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedServers.map((row) => (
                          <tr key={`server-${row.serverKey || 'unassigned'}`}>
                            {SERVER_COLUMNS.map((col) => (
                              <td
                                key={`sv-${row.serverKey}-${col.key}`}
                                style={{
                                  textAlign: col.kind === 'text' ? 'left' : 'right',
                                  background: col.perks ? 'rgba(14, 165, 233, 0.08)' : undefined,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {formatServerCell(row, col)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ color: 'rgba(15, 23, 42, 0.6)', fontSize: '0.82rem', marginTop: 8, lineHeight: 1.5 }}>
                    <strong>Active hours is a derived proxy, not a time clock.</strong> It is the span from a server's first to
                    last order each day, floored at {MIN_ACTIVE_HOURS}h, so it under-counts setup and close time. Tips are not
                    included anywhere on this page — staff orders do not record them.
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginTop: 16 }}>
                    <div style={{ overflowX: 'auto' }}>
                      <h4 style={{ margin: '0 0 8px' }}>Order edits by user</h4>
                      {additionRows.length === 0 ? (
                        <div style={{ color: 'rgba(15, 23, 42, 0.7)' }}>No order edits in this range.</div>
                      ) : (
                        <>
                        <div style={{ width: '100%', height: rowChartHeight(editsChartData.length * 2, 200) }}>
                          <ResponsiveContainer>
                            <BarChart data={editsChartData} layout="vertical" margin={{ left: 8, right: 20 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => formatMajor(Number(v) || 0)} />
                              <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 11 }} />
                              <Tooltip contentStyle={{ borderRadius: 12 }} formatter={(v) => formatMajor(Number(v) || 0)} />
                              <Legend />
                              <Bar dataKey="valueAdded" name="Value added" fill="#22c55e" radius={[0, 8, 8, 0]} isAnimationActive={false} />
                              <Bar dataKey="valueRemoved" name="Value removed" fill="#ef4444" radius={[0, 8, 8, 0]} isAnimationActive={false} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                        <table className="admin-table" style={{ width: '100%' }}>
                          <thead>
                            <tr>
                              <th>User</th>
                              <th style={{ textAlign: 'right' }}>Own tickets</th>
                              <th style={{ textAlign: 'right' }}>Others' tickets</th>
                              <th style={{ textAlign: 'right' }}>Value added</th>
                              <th style={{ textAlign: 'right' }}>Value removed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {additionRows.map((a) => (
                              <tr key={`add-${a.actorKey}`}>
                                <td>{a.name}</td>
                                <td style={{ textAlign: 'right' }}>{a.ownOrdersTouched.toLocaleString()}</td>
                                <td style={{ textAlign: 'right' }}>{a.othersOrdersTouched.toLocaleString()}</td>
                                <td style={{ textAlign: 'right' }}>{formatMoneyFromCents(a.valueAddedCents)}</td>
                                <td style={{ textAlign: 'right' }}>{formatMoneyFromCents(a.valueRemovedCents)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        </>
                      )}
                    </div>

                    <div>
                      <h4 style={{ margin: '0 0 8px' }}>Top items per server</h4>
                      <div style={{ display: 'grid', gap: 14 }}>
                        {serverTopItemsChart.map((s) => (
                          <div key={`svtop-${s.serverKey || 'unassigned'}`}>
                            <div style={{ fontWeight: 600 }}>{s.serverName}</div>
                            {s.rows.length === 0 ? (
                              <div style={{ color: 'rgba(15, 23, 42, 0.7)', fontSize: '0.9rem' }}>{DASH}</div>
                            ) : (
                              <>
                                <div style={{ width: '100%', height: rowChartHeight(s.rows.length, 110) }}>
                                  <ResponsiveContainer>
                                    <BarChart data={s.rows} layout="vertical" margin={{ left: 4, right: 26 }}>
                                      <XAxis type="number" hide />
                                      <YAxis type="category" dataKey="label" width={130} tick={{ fontSize: 11 }} />
                                      <Tooltip
                                        contentStyle={{ borderRadius: 12 }}
                                        formatter={(v, name) => (name === 'Qty' ? Number(v) : formatMajor(Number(v) || 0))}
                                        labelFormatter={(label, payload) => {
                                          const p = Array.isArray(payload) && payload.length ? payload[0]?.payload : null;
                                          return p?.fullName || label;
                                        }}
                                      />
                                      <Bar dataKey="revenue" name="Revenue" fill="#6366f1" radius={[0, 8, 8, 0]} isAnimationActive={false}>
                                        <LabelList dataKey="quantity" position="right" formatter={(v) => `×${v}`} style={{ fontSize: 11 }} />
                                      </Bar>
                                    </BarChart>
                                  </ResponsiveContainer>
                                </div>
                                <div style={{ color: 'rgba(15, 23, 42, 0.7)', fontSize: '0.86rem' }}>
                                  {s.rows.map((it) => `${it.fullName} ×${it.quantity}`).join(' · ')}
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}
